import classNames from 'classnames'
import {
    lazy,
    memo,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import os from '../../../data/selectOptions/os.json'
import serverMetrics from '../../../data/serverMetrics.json'
import {ReminderIcon} from '../../../icons'
import {routes} from '../../../routes'
import {getServerResourceMetrics, setRulesets} from '../../../stores/server-monitoring'
import NavigationHelper from '../../../utils/navigation'
import {isNewFeatureEnabled} from '../../../utils/newFeature'
import {osIcons} from '../../../utils/osIcons'
import {SimpleBlueButton} from '../../core/button'
import Input from '../../core/input'
import ContentLoader from '../../core/loader'
import Modal from '../../core/modal'
import Tooltip from '../../core/tooltip'
import RulesetListView from '../rulesetIntervalForm/RulesetListView'
import RulesetForm from '../rulesetIntervalForm/rulesetForm'
import modalTypes from './modalTypes.json'
import Treemap from './serverCharts/Treemap'
import ConfigFileComponent from './serverConfig/ConfigFileComponent'
import text from './text.json'

const ExpandedLineChart = lazy(() => import('./serverCharts/ExpandedLineChart'))

const FilterOptions = memo(({value, setValue}) => (
    <div className="filter-options">
        <Input
            value={value}
            inputClassName="filter-input"
            type="number"
            placeholder={text.top_processes}
            name="top-processes"
            onChange={(e) => setValue(e.target.value)}
        />
    </div>
))

const Modals = ({
    isVisible,
    activeModal,
    setActiveModal,
    showBackButton,
    setShowBackButton,
    uiConfig = {},
    rulesetConfig = {},
    serverMetricsConfig = {}
}) => {
    const {
        isDarkMode,
        telemetryUrl,
        selectedOsOption,
        selectedMetricTypes,
        serverResource,
        isMetricsDataRetrieving,
        isResourceFetching
    } = uiConfig

    const {
        rulesetData,
        rulesetFormInitialValues,
        usageLimits,
        rulesetTypeOptions,
        isCronJobRuleset
    } = rulesetConfig

    const {
        selectedMetric,
        instructionModalContent
    } = serverMetricsConfig

    const dispatch = useDispatch()
    const [originalProcessesData, setOriginalProcessesData] = useState([])
    const [processedTypeData, setProcessedTypeData] = useState({})
    const [inputValue, setInputValue] = useState(10)

    const {categories, commonMetricsInfo, groupedData, absoluteAdditionalMetrics} = useSelector((state) => {
        const monitoringData = selectedMetric?.value ? state.server_monitoring[selectedMetric.value] : {}
        return {
            categories: monitoringData?.categories || [],
            commonMetricsInfo: monitoringData?.commonMetricsInfo || [],
            groupedData: monitoringData?.groupedData || {},
            absoluteAdditionalMetrics: monitoringData?.absoluteAdditionalMetrics || []
        }
    })

    const closeModal = useCallback(() => {
        setActiveModal({type: null, metric_type: null, modal_id: null})
        setShowBackButton(false)
    }, [setActiveModal, setShowBackButton])

    const showModal = useCallback((type) => {
        setActiveModal((prev) => ({...prev, type, metric_type: selectedMetric?.value}))
    }, [selectedMetric?.value, setActiveModal])

    const transformData = useCallback((data, metricType) => {
        return data?.filter(item => metricType !== serverMetrics.processes_unified.value || item?.data?.[0] !== 0)
            .sort((a, b) => (b?.data?.[0] ?? 0) - (a?.data?.[0] ?? 0))
    }, [])

    const zoomModalData = useMemo(() => transformData(groupedData?.[activeModal.modal_id], selectedMetric?.value),
        [groupedData,
            activeModal.modal_id,
            selectedMetric?.value,
            transformData])

    const modalId = useMemo(() => {
        if ([modalTypes.expanded_chart, modalTypes.zoom_chart].includes(activeModal?.type)) return 'expanded-charts-modal'
        if (activeModal?.type === modalTypes.instruction) return 'rulesets-modal'
        return ''
    }, [activeModal?.type])

    const IconComponent = osIcons?.[selectedOsOption?.icon]

    const modalTitle = useMemo(() => {
        switch (activeModal?.type) {
        case modalTypes.expanded_chart:
            return selectedMetric?.title
        case modalTypes.instruction:
            return (
                <div className={showBackButton ? 'd-flex flex-column align-items-center' : ''}>
                    <span className="d-flex align-items-center">
                        {text.instructions}
                        <Tooltip
                            content={selectedOsOption?.label || os.centos.label}
                            trigger="mouseenter"
                            placement="bottom"
                            noButton
                            interactive>
                            <IconComponent height={selectedOsOption?.height || 28} />
                        </Tooltip>
                    </span>
                    <span className={classNames('os-desc', {dark: isDarkMode})}>
                        {text.not_right_os_text}
              Click <Link to={NavigationHelper.getNavigationUrl(routes.edit_server_monitoring.path, ':id', serverResource?.id)}>here</Link> to update.
                    </span>
                </div>
            )
        case modalTypes.zoom_chart:
            return `${serverMetrics.processes_unified.charts?.[activeModal?.modal_id]?.label} (${zoomModalData?.[0]?.unit})`
        default:
            return 'Ruleset details'
        }
    }, [activeModal?.type, selectedMetric?.title, selectedOsOption, isDarkMode, serverResource?.id, zoomModalData])

    const modalDataProps = useMemo(() => ({
        selectedMetric,
        categories: selectedMetric?.value === serverMetrics.processes_unified.value ?
            processedTypeData.categories :
            categories,
        commonMetricsInfo,
        rawMetricSeries: selectedMetric?.value === serverMetrics.processes_unified.value ?
            processedTypeData.groupedData :
            groupedData[activeModal.modal_id],
        absoluteMetrics: absoluteAdditionalMetrics,
        isDarkMode,
        isResourceFetching,
        isMetricsDataRetrieving,
        serverResource,
        selectedOsOption,
        selectedMetricTypes
    }), [selectedMetric,
        processedTypeData,
        categories,
        commonMetricsInfo,
        groupedData,
        activeModal.modal_id,
        absoluteAdditionalMetrics,
        isDarkMode,
        isResourceFetching,
        isMetricsDataRetrieving,
        serverResource,
        selectedOsOption,
        selectedMetricTypes])

    const contentClass = classNames({
        'expanded-chart': activeModal?.type === modalTypes.expanded_chart,
        'overflow-auto instruction-modal': activeModal?.type !== modalTypes.expanded_chart,
        'instruction-modal-container': activeModal?.type === modalTypes.instruction
    })

    const hasBack = useMemo(() =>
        (showBackButton && [modalTypes.ruleset_list, modalTypes.instruction].includes(activeModal?.type)) ||
    [modalTypes.ruleset_form, modalTypes.ruleset_form_edit].includes(activeModal?.type), [activeModal?.type, showBackButton])

    const backtoPreviousModal = useCallback(() => {
        if ([modalTypes.ruleset_list, modalTypes.instruction].includes(activeModal?.type)) {
            showModal(modalTypes.expanded_chart)
        } else {
            showModal(modalTypes.ruleset_list)
        }
    }, [activeModal?.type, showModal])

    const handleRulesetsModal = useCallback(() => {
        if (isNewFeatureEnabled()) {
            showModal(modalTypes.ruleset_list)
            setShowBackButton(true)
        }
    }, [showModal, setShowBackButton])

    const fetchProcessedMetrics = useCallback(async() => {
        const {categories, groupedData} = await dispatch(getServerResourceMetrics(true, null, selectedMetric?.value))
        setOriginalProcessesData(groupedData?.[activeModal.modal_id])
        setProcessedTypeData({categories, groupedData: groupedData?.[activeModal.modal_id]})
    }, [dispatch, selectedMetric?.value, activeModal.modal_id])

    const filterChartData = useCallback(() => {
        setProcessedTypeData((prev) => ({
            ...prev,
            groupedData: originalProcessesData?.slice(0, inputValue)
        }))
    }, [originalProcessesData, inputValue])

    const resetChartData = useCallback(() => {
        setProcessedTypeData((prev) => ({
            ...prev,
            groupedData: originalProcessesData
        }))
    }, [originalProcessesData])

    useEffect(() => {
        if (activeModal.type === modalTypes.expanded_chart && selectedMetric?.value === serverMetrics.processes_unified.value) {
            fetchProcessedMetrics()
        }
    }, [activeModal.type, selectedMetric?.value, fetchProcessedMetrics])

    return (
        <Modal
            isOpen={isVisible}
            id={modalId}
            contentClassName={contentClass}
            closeModal={closeModal}
            hasBack={hasBack}
            backtoPreviousModal={backtoPreviousModal}
            title={modalTitle}
        >
            {activeModal?.type === modalTypes.ruleset_list && isNewFeatureEnabled() && (
                <RulesetListView
                    data={rulesetData}
                    rulesetUsageLimit={usageLimits.incident_ruleset}
                    isCronJobRuleset={isCronJobRuleset}
                    showFormViewModal={() => showModal(modalTypes.ruleset_form)}
                />
            )}

            {activeModal?.type === modalTypes.instruction && (
                <ConfigFileComponent
                    url={telemetryUrl}
                    data={instructionModalContent}
                    language={selectedOsOption?.type || os.centos.type}
                    selectedMetricTypes={selectedMetricTypes}
                />
            )}

            {[modalTypes.ruleset_form, modalTypes.ruleset_form_edit].includes(activeModal?.type) && (
                <RulesetForm
                    rulesetFormInitialValues={rulesetFormInitialValues}
                    rulesetTypeOptions={rulesetTypeOptions}
                    setRulesetValues={(data) => dispatch(setRulesets(data))}
                    closeModal={closeModal}
                    onCancel={() => showModal(modalTypes.ruleset_list)}
                    isCronJobRuleset={isCronJobRuleset}
                />
            )}

            {activeModal?.type === modalTypes.expanded_chart && (
                <div className={classNames('expanded-charts', {dark: isDarkMode})}>
                    <span className="chart-subtitle">
                        <Tooltip content={text.coming_soon} placement="top" trigger="mouseenter">
                            <span className="coming-soon-text" onClick={handleRulesetsModal}>
                                {text.set_rulesets}
                                <ReminderIcon />
                            </span>
                        </Tooltip>
                        {selectedMetric?.value === serverMetrics.processes_unified.value && (
                            <>
                                <FilterOptions value={inputValue} setValue={setInputValue} />
                                <SimpleBlueButton onClick={filterChartData}>{text.filter}</SimpleBlueButton>
                                <SimpleBlueButton onClick={resetChartData} outline>{text.reset}</SimpleBlueButton>
                            </>
                        )}
                    </span>
                    <Suspense fallback={<ContentLoader />}>
                        <ExpandedLineChart {...modalDataProps} activeModal={activeModal} />
                    </Suspense>
                </div>
            )}

            {activeModal?.type === modalTypes.zoom_chart && (
                <Treemap
                    selectedMetric={selectedMetric}
                    isInModal
                    data={zoomModalData}
                    commonMetricsInfo={commonMetricsInfo}
                    isDarkMode={isDarkMode}
                    serverResource={serverResource}
                />
            )}
        </Modal>
    )
}

export default memo(Modals)
